import * as Yup from "yup";
import {reg_mail} from "../RegexConstants";
import dayjs from "dayjs";

// ERROR MESSAGES KEY
const requiredError = "mandatory_field";
const maxLengthError = "max_length_error_field";
const invalidEmail = "field_email_invalid";
const positiveNumber = "field_number_positive";
const positiveOrZeroNumber = "field_number_positive_or_zero";
const invalidNumber = "invalid_number_field";
const ibanRegexError = "invalid_iban_field";
const invalidLengthEmail = "login_invalid_length";
const invalidPassword = "password_invalid";
const invalidDate = "invalid_date_field";
const invalidSiren = "invalid_siren_field";
const invalidTime = "invalid_time_field";

// REGEX
const vatRegex = /^(ATU\d{8}|BE0\d{9}|BG\d{9,10}|CHE\d{9}(TVA)?|CY\d{8}[A-Z]|CZ\d{8,10}|DE\d{9}|DK\d{8}|EE\d{9}|EL\d{9}|ES[A-Z0-9]\d{7}[A-Z0-9]|FI\d{8}|FR[A-Z0-9]{2}\d{9}|GB\d{9}|GB\d{12}|HR\d{11}|HU\d{8}|IE\d{7}[A-W]|IT\d{11}|LT\d{9}|LT\d{12}|LU\d{8}|LV\d{11}|MT\d{8}|NL[A-Z0-9]{12}|PL\d{10}|PT\d{9}|RO\d{2,10}|SE\d{12}|SI\d{8}|SK\d{10})$/;


// STRINGS
export const yupRequiredString: Yup.StringSchema = Yup.string().required(requiredError).typeError(requiredError);
export const yupNullableString: Yup.StringSchema = Yup.string().nullable();
export const yupRequireStringMaxLength = (max: number): Yup.StringSchema => Yup.string().required(requiredError).typeError(requiredError).max(max, maxLengthError);
export const yupRequiredStringIban: Yup.StringSchema = Yup.string().matches(/^[A-Z]{2}\d{2}[A-Za-z0-9]{1,30}$/, ibanRegexError);
export const yupRequiredPassword = Yup.string().required(requiredError).typeError(requiredError);
export const yupRequiredValidPassword = Yup.string().required(requiredError).typeError(requiredError).matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#?!@$%^&*-])[A-Za-z\d#?!@$%^&*-_]{8,}$/, invalidPassword);

export const yupCorrectEmail = Yup.string().matches(reg_mail, invalidEmail);
export const yupNullableEmail = yupCorrectEmail.nullable();
export const yupRequiredEmail = (min: number, max: number): Yup.StringSchema =>
  yupCorrectEmail.required(requiredError).typeError(requiredError).min(min, invalidLengthEmail).max(max, invalidLengthEmail);
export const yupDateDayMatchMonth = (dayField: string, monthField: string, message?: string): Yup.StringSchema => {
  return Yup.string().test("isDayMatchMonth", message, function () {
    const day = this.parent[dayField];
    const month = this.parent[monthField];
    return day && month && dayjs(`2021-${month}-${day}`, "YYYY-MM-DD", true).isValid() || this.createError({message: message || invalidDate});
  });
}

// NUMBERS
export const yupRequiredNumber = Yup.number().typeError(requiredError).required(requiredError);
export const yupRequiredPositiveNumber = Yup.number().typeError(requiredError).required(requiredError).moreThan(0, positiveNumber);
export const yupPositiveNumber = Yup.number().moreThan(0, positiveNumber);
export const yupRequiredPositiveOrZeroNumber = Yup.number().typeError(requiredError).required(requiredError).min(0, positiveOrZeroNumber);
export const getRequiredNumberForCondition = (field: string, condition: unknown): Yup.NumberSchema =>
  Yup.number().nullable().when(field, {is: condition, then: yupRequiredNumber});

export const getRequiredPositiveNumberForCondition = (field: string, condition: unknown): Yup.NumberSchema =>
  Yup.number().nullable().when(field, {is: condition, then: yupRequiredPositiveNumber});

export const yupNotRequiredSiren = Yup.string().matches(/^\d{9}$/, invalidSiren).nullable();

export const yupVATNumber = Yup.string().matches(vatRegex, "invalid_vat_number_field").nullable();

export const getRequiredStringForCondition = (field: string | string[], condition: unknown): Yup.StringSchema => {
  return Yup.string().nullable().when(field, {is: condition, then: yupRequiredString})
};

export const getRequiredVATNumberForCondition = (field: string | string[], condition: unknown): Yup.StringSchema =>
  Yup.string().nullable().when(field, {is: condition, then: yupVATNumber});
export const yupRequiredNumberBetween = (min: number, max: number): Yup.NumberSchema =>
  Yup.number().required(requiredError).typeError(requiredError).min(min, invalidNumber).max(max, invalidNumber);

// DATE
export const yupRequiredDate = Yup.date().required(requiredError).typeError(requiredError);
export const getRequiredDateForCondition = (field: string | string[], condition: unknown): Yup.DateSchema => {
  return Yup.date().nullable().when(field, {is: condition, then: yupRequiredDate, otherwise: Yup.date().nullable()});
}
export const yupDateIsBefore = (otherFieldName: string, message?: string): Yup.DateSchema =>
  Yup.date().test("isBeforeDate", message, function (value) {
    const otherDate = this.parent[otherFieldName];
    return otherDate && otherDate < value || this.createError({message: message || invalidDate});
  });
export const yupDateDayGapCorrect = (startDateField: string, endDateField: string, message?: string, monthGap = 1): Yup.DateSchema =>
  Yup.date().test("isDateDayGapCorrect", message, function () {
    const startDate = this.parent[startDateField];
    const endDate = this.parent[endDateField];
    return startDate && endDate && dayjs(endDate).subtract(monthGap, "month") < startDate || this.createError({message: message || invalidDate});
  });

// TIME
export const yupRequiredTime: Yup.StringSchema = Yup.string()
  .required(requiredError)
  .typeError(requiredError)
  .matches(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, invalidTime);

// BOOLEANS
export const yupRequiredBoolean: Yup.BooleanSchema = Yup.boolean().required(requiredError).typeError(requiredError);

export const getRequiredBooleanForCondition = (field: string, condition: unknown): Yup.BooleanSchema =>
  Yup.boolean().nullable().when(field, {is: condition, then: yupRequiredBoolean});

// ARRAYS
export const yupRequiredArray = (min: number): Yup.ArraySchema<Yup.AnySchema> => Yup.array().required(requiredError).min(min, requiredError);

// OBJECTS
export const yupNotRequiredObject = Yup.object().nullable().notRequired();
